import {
    Button,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField
} from '@material-ui/core';
import {
    PAYMENT_METHODS,
    PAYMENT_METHODS_BALANCE_FILLING,
    usePaymentMethods
} from '../../hooks/usePaymentMethod';
import { useEffect, useState } from 'react';
import PaymentFormCard from '../../pages/payment/components/PaymentFormCard';
import { loadStripe } from '@stripe/stripe-js';
import config from '../../config';
import PaymentFormOnhouse from '../../pages/payment/components/PaymentFormOnhouse';
import PaymentFormCash from '../../pages/payment/components/PaymentFormCash';
import PaymentFormCheck from '../../pages/payment/components/PaymentFormCheck';
import { useReduxState } from '../../hooks/useReduxState';
import { doSwaggerCall } from '../../hooks/useSwaggerClient';
import { Alert } from '@material-ui/lab';

const stripePromise = loadStripe(config.stripeKey);

function FillBalanceForm({ onBalanceChange, amountHint = 0, balanceSlug }) {
    const reduxState = useReduxState();

    const [isFilling, setIsFilling] = useState(false);
    const [fillingError, setFillingError] = useState(false);
    const [note, setNote] = useState('');
    const [amount, setAmount] = useState(Math.ceil(100 * amountHint) / 100);
    // PAYMENT_METHODS_BALANCE_FILLING vs current methods
    const clientPaymentMethods = usePaymentMethods();
    const fillPaymentMethods = clientPaymentMethods.filter(method =>
        PAYMENT_METHODS_BALANCE_FILLING.includes(method.methodtype)
    );
    const [selectedMethod, setSelectedMethod] = useState(() => {
        return fillPaymentMethods[0]?.slug || '';
    });
    const method = fillPaymentMethods.find(method => method.slug === selectedMethod);

    useEffect(() => {
        setAmount(Math.ceil(100 * amountHint) / 100);
    }, [amountHint]);

    const onFillBalance = () => {
        setIsFilling(true);

        const userId =
            reduxState.user.customer.id === reduxState.user.user.id
                ? 'me'
                : reduxState.user.customer.id;

        // mimick the createBooking here
        doSwaggerCall('balance', 'fillBalance', {
            userId,
            methodSlug: balanceSlug,
            newFill: {
                amount: parseFloat(amount),
                usedPaymentMethod: selectedMethod,
                stripe: {
                    token: reduxState.user.customer.stripe && reduxState.user.customer.stripe.id
                },
                note,
                paymentIntent: null
            }
        })
            .then(() => {
                setIsFilling(false);
                onBalanceChange();
            })
            .catch(e => {
                setIsFilling(false);
                setFillingError(e.message);
            });
    };

    const fillSubmitDisabled =
        isFilling || !amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0;

    return (
        <Paper className="colorcard mt-2" elevation={2}>
            <h4 className="fullheadline mb-2">Fill up balance</h4>
            <div style={{ padding: '16px' }}>
                <Grid container spacing={2}>
                    {fillingError !== false && (
                        <Grid item xs={12}>
                            <Alert severity="error">
                                <div style={{ color: 'red' }}>{fillingError}</div>
                            </Alert>
                        </Grid>
                    )}
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            variant="standard"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                            label="Amount"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel id="fill-select">Method to fill up balance</InputLabel>
                            <Select
                                id="fill-select"
                                value={selectedMethod}
                                label="Method to fill up balance"
                                onChange={e => setSelectedMethod(e.target.value)}
                            >
                                {fillPaymentMethods.map(method => (
                                    <MenuItem key={method.slug} value={method.slug}>
                                        {method.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        {method?.methodtype === PAYMENT_METHODS.card && (
                            <PaymentFormCard
                                stripePromise={stripePromise}
                                user={reduxState.user?.user}
                                note={note}
                                setNote={setNote}
                            />
                        )}
                        {method?.methodtype === PAYMENT_METHODS.onhouse && (
                            <PaymentFormOnhouse note={note} setNote={setNote} />
                        )}
                        {method?.methodtype === PAYMENT_METHODS.cash && (
                            <PaymentFormCash note={note} setNote={setNote} />
                        )}
                        {method?.methodtype === PAYMENT_METHODS.check && (
                            <PaymentFormCheck note={note} setNote={setNote} />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={fillSubmitDisabled}
                            onClick={onFillBalance}
                        >
                            Fill Balance
                        </Button>
                    </Grid>
                </Grid>
            </div>
        </Paper>
    );
}

export default FillBalanceForm;
